import { styled } from "goober";
import { useState } from "react";
import { queries } from "../../../../shared/queries";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useNow } from "../../../hooks/useNow";
import { useQuery } from "@rocicorp/zero/react";
import {
  useMakeUrl,
  navigate,
  routes,
  useCurrentUrl,
  useRouteMatch,
} from "../../../lib/urls";
import { Button } from "../../Button";
import { Dropdown } from "../../Dropdown";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import { ListTable } from "../../ListTable";
import { StatusBadge } from "../../StatusBadge";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";
import { Cell, IdentityCell } from "../IdentityCell";
import { Container, Header, ScrollArea } from "../shared";
import { DeleteSsoProviderModal } from "./SsoProviderChanges";
import { ExistingSsoProvider } from "./ExistingSsoProvider";
import {
  identityProvider,
  identityProviderForIssuer,
  identityProviders,
} from "./identityProviders";
import { IdpLogo } from "./IdpLogo";
import { StatusCell } from "./StatusCell";
import { ssoProviderStatus } from "./providerStatus";
import { NewSsoProvider } from "./NewSsoProvider";
import { RevokeSessionsModal, ScimTokenModal } from "./SsoModals";

const SYNC_MODE_LABELS: Record<string, string> = {
  off: "Off",
  jit: "JIT",
  jit_users_and_groups: "JIT + groups",
  scim: "SCIM",
};

const HeaderActions = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

export function SSOSettings() {
  const makeUrl = useMakeUrl();
  const [providers] = useQuery(queries.ssoProviders());
  const now = useNow(60 * 60 * 1000);
  const canManageSSO = useHasAccess("tenant:sso_manage");
  const { provider: selectedId } = useCurrentUrl(routes.settings.sso);
  const [isNew] = useRouteMatch(routes.settings.sso.new);
  const { idp } = useCurrentUrl(routes.settings.sso.new);
  // Lives here so a token minted while registering a provider survives
  // navigating back to the list.
  const [scimToken, setScimToken] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const deleting = providers.find((p) => p.id === deletingId);

  if (!canManageSSO) {
    return (
      <Container>
        <Header>
          <Text variant="text-regular-md">Single sign-on</Text>
        </Header>
        <ScrollArea>
          <div style={{ padding: 24 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              Only admins can configure SSO
            </Text>
          </div>
        </ScrollArea>
      </Container>
    );
  }

  const selected = providers.find((provider) => provider.id === selectedId);

  function backToList() {
    navigate(
      routes.settings.sso,
      { provider: "", tab: "settings" },
      { replace: true }
    );
  }

  const items = providers.map((provider) => ({
    key: provider.id,
    link: makeUrl(routes.settings.sso, {
      provider: provider.id,
      tab: "settings",
    }),
    name: (
      <IdentityCell
        title={provider.providerId}
        subtitle={provider.issuer}
        icon={<IdpLogo idp={identityProviderForIssuer(provider.issuer)} />}
        badge={
          provider.isPrimary ? (
            <Tooltip content="Used for tenant sign-in">
              <StatusBadge color="green">Primary</StatusBadge>
            </Tooltip>
          ) : undefined
        }
      />
    ),
    protocol: <Cell value={provider.protocol.toUpperCase()} />,
    domain: <Cell value={provider.domain} />,
    syncMode: (
      <Cell value={SYNC_MODE_LABELS[provider.syncMode ?? "off"] ?? "Off"} />
    ),
    status: <StatusCell status={ssoProviderStatus(provider, now)} />,
    actions: (hasMouse: boolean) => {
      const menuItems = [
        {
          type: "button" as const,
          left: <Icon name="trash" size={16} />,
          label: "Delete",
          onClick: () => setDeletingId(provider.id),
        },
      ];
      return (
        <div
          style={{ opacity: hasMouse ? 1 : 0 }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Dropdown
            opener={<Button variant="secondary" icon="more" />}
            align="bottom"
            minWidth={180}
            sections={[{ items: menuItems }]}
          />
        </div>
      );
    },
  }));

  const modals = (
    <>
      {selected && (
        <ExistingSsoProvider
          key={selected.id}
          provider={selected}
          onGeneratedScimToken={setScimToken}
          onDelete={() => setDeletingId(selected.id)}
          onClose={backToList}
        />
      )}
      {isNew && (
        <NewSsoProvider
          key={idp}
          idp={identityProvider(idp)}
          takenProviderIds={providers.map((p) => p.providerId)}
          onGeneratedScimToken={setScimToken}
          onClose={backToList}
        />
      )}
      <ScimTokenModal token={scimToken} onClose={() => setScimToken(null)} />
      {deleting && (
        <DeleteSsoProviderModal
          provider={deleting}
          onClose={() => setDeletingId(null)}
          onDeleted={() => setScimToken(null)}
        />
      )}
      <RevokeSessionsModal
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
      />
    </>
  );

  return (
    <Container>
      <Header style={{ paddingRight: 12 }}>
        <Text variant="text-regular-md">Single sign-on</Text>
        <HeaderActions>
          <Tooltip content="Revoke all sessions">
            <Button
              size="l"
              variant="secondary"
              icon="output"
              aria-label="Revoke all sessions"
              onClick={() => setConfirmRevoke(true)}
            />
          </Tooltip>
          <Dropdown
            opener={<Button size="l" icon="plus" />}
            align="bottom"
            minWidth={200}
            sections={[
              {
                label: "Add provider",
                items: identityProviders.map((provider) => ({
                  type: "button" as const,
                  left: <IdpLogo idp={provider} />,
                  label: provider.name,
                  onClick: () =>
                    navigate(routes.settings.sso.new, { idp: provider.id }),
                })),
              },
            ]}
          />
        </HeaderActions>
      </Header>

      <ScrollArea>
        {providers.length === 0 ? (
          <EmptyState
            iconName="shield-check"
            title="No SSO providers"
            description="Add an identity provider so people can sign in with SSO."
          />
        ) : (
          <ListTable
            headers={{
              name: "Provider",
              protocol: "Protocol",
              domain: "Domain",
              syncMode: "Provisioning",
              status: "Status",
              actions: "",
            }}
            items={items}
            columns={{ status: "content" }}
          />
        )}
      </ScrollArea>

      {modals}
    </Container>
  );
}
